import { useEffect, useMemo, useState } from "react";

import {
  ALL_PRODUCT_TYPES,
  BLACK_HEX,
  CrossKindProduct
} from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { toExpectedTypeWellBackendProductName } from "utils/arps/productMapping";

import { useChartState } from "components/shared/charts";
import { useChartDependencies } from "components/shared/charts/hooks";

import { EntityKind } from "../../../models/entityKind";
import { getProductType, productStringToProductTypeEnum, uid } from "../../../utils";
import { ProductTypeEnum } from "../models/ArpsSegment";
import { ArpsSegmentToSegmentDto as arpsSegmentToSegmentDto } from "../utils/arpsUtils";
import { groupArpsSegmentsByProduct } from "../utils/groupArpsSegmentsByProduct";
import { getTypeWellSeries } from "../utils/typeWellUtils";
import { arpsWasm } from "../utils/wasmLoader";
import { useFetchForecastArray } from "./useFetchForecastArray";

// WASM returns product names that match ALL_PRODUCT_TYPES keys (e.g., "OnePlusWOR" for 1+WOR)
const WASM_ONE_PLUS_WOR_PRODUCT = "OnePlusWOR";

export default function useTypeWellSeries(normalizeTypeWell: boolean) {
  const { checkedGlobalTypeWells } = useChartDependencies(EntityKind.Well);
  const { response, settings: chartSettings, entityKind } = useChartState();

  const [typeWellSeries, setTypeWellSeries] = useState([]);

  //type well id only updates when the store value changes
  //this allows us to identify type wells with new data vs no changes
  const typeWellId = useMemo(() => uid(), [checkedGlobalTypeWells]);
  const { data: forecastArrays } = useFetchForecastArray({
    ids: Array.isArray(checkedGlobalTypeWells)
      ? checkedGlobalTypeWells.map((tw) => tw.id)
      : []
  });

  useEffect(() => {
    updateTypeWellSeries();
  }, [
    typeWellId,
    normalizeTypeWell,
    chartSettings.chartType,
    chartSettings.product,
    chartSettings.timeStep,
    chartSettings.useNormalizeBy,
    chartSettings.normalizeBy,
    response?.layout?.yAxis?.title,
    forecastArrays
  ]);

  function updateTypeWellSeries() {
    const chartType = chartSettings.chartType;
    const isNonTotalDeclineChart =
      chartType &&
      (chartType.includes("Rate") || chartType.includes("Cum Time")) &&
      !chartType.includes("Total");
    if (
      !arpsWasm ||
      !checkedGlobalTypeWells ||
      !chartSettings.product ||
      !isNonTotalDeclineChart ||
      entityKind === EntityKind.Facility
    ) {
      setTypeWellSeries([]);
      return;
    }

    const titles = {};
    const temporaryTypeWellSeries = [];
    const product = getProductType(chartSettings.product);

    const crossKindProductKey =
      CrossKindProduct.find((prd) => prd.items.some((item) => item.key === product.key))
        ?.group ?? undefined;

    for (const tw of checkedGlobalTypeWells) {
      const arpsGroupedByProduct = groupArpsSegmentsByProduct(tw.arps);

      let selectedProductSegment = crossKindProductKey
        ? arpsGroupedByProduct[crossKindProductKey.toUpperCase()]
        : arpsGroupedByProduct[product.key.toUpperCase()];
      if (
        selectedProductSegment == null &&
        (product.key === ALL_PRODUCT_TYPES.Oil.key ||
          crossKindProductKey === ALL_PRODUCT_TYPES.Oil.key)
      ) {
        selectedProductSegment =
          "COND." in arpsGroupedByProduct
            ? arpsGroupedByProduct["COND."]
            : arpsGroupedByProduct["CONDENSATE"];
      }

      // Ignore this check if we have a selected production segment

      const hasForecastArray =
        forecastArrays &&
        forecastArrays.forecastArray[tw.id] &&
        forecastArrays.forecastArray[tw.id].length > 0;
      if (!tw.arps?.length && !hasForecastArray) {
        continue;
      }

      // Don't render decline type for ramp-up segment
      const declineSegments = [...tw.arps];

      const dto = arpsSegmentToSegmentDto(arpsWasm, declineSegments);

      if (dto.length == 0 && !hasForecastArray) {
        continue;
      }

      const forecastConstantsWithUnit = (tw.constants ?? [])
        .map((fconst) => {
          const productEnum = productStringToProductTypeEnum(fconst.product);
          //fix the product string so it matches the enum name
          const productString = ProductTypeEnum[productEnum];
          let convertProductStringForBackend =
            toExpectedTypeWellBackendProductName(productString);
          if (convertProductStringForBackend == "total shrinkage inlet") {
            convertProductStringForBackend = "TotalShrinkageInlet"; //to match wasm name
          }
          if (!fconst.unit) {
            return null;
          }
          return {
            ...fconst,
            unit: fconst.unit,
            product: convertProductStringForBackend
          };
        })
        .filter((x) => x != null);

      try {
        const normalize = Object.assign({}, chartSettings.normalizeBy, {
          normalize: chartSettings.useNormalizeBy,
          normalizeTypeWell: normalizeTypeWell,
          // some old Chart widgets will miss those settings by default, so make sure they are set
          // since arpsWasm required these settings, leaving them empty will cause wasm to throw error (and default to default normalization, which is no normalization)
          useMultilinearNormalization:
            chartSettings.normalizeBy?.useMultilinearNormalization ?? false,
          threshold: chartSettings.normalizeBy?.threshold ?? 0.0,
          lowerScalar: chartSettings.normalizeBy?.lowerScalar ?? 0.0,
          higherScalar: chartSettings.normalizeBy?.lowerScalar ?? 0.0
        });

        const wellData = normalize.normalize
          ? {
              length: tw.wellData?.HzLength ?? 0,
              stage: tw.wellData?.Stage ?? 0,
              stage_spacing: tw.wellData?.StageSpacing ?? 0,
              proppant: tw.wellData?.Proppant ?? 0,
              proppant_intensity: tw.wellData?.ProppantIntensity ?? 0,
              additional_data: tw.wellData?.AdditionalData ?? []
            }
          : {
              length: 0,
              stage: 0,
              stage_spacing: 0,
              proppant: 0,
              proppant_intensity: 0,
              additional_data: []
            };
        const settings = {
          isDaily: chartSettings?.timeStep === TimeStep.Day,
          isCumTime: chartType === EvaChart.CumTime
        };
        const seriesData = hasForecastArray
          ? arpsWasm.getForecastFromArray(
              forecastArrays.forecastArray[tw.id],
              normalize,
              wellData,
              {},
              settings
            )
          : arpsWasm.getForecastFromArpsSegments(
              dto,
              forecastConstantsWithUnit,
              normalize,
              wellData,
              {},
              settings
            );

        for (const series of seriesData) {
          //added indexOf check here because "Total Fluid" key does not match product name
          //from series.product (Total), but Oil and Water should not match for Oil and Water Cut
          //regex removes space or + to match product. Oil+Cond+C5 Yields Sales becomes OilCondC5YieldsSales
          const isMismatchedProduct =
            series.product !== product.key.replace(/\s|\+/g, "");
          const isTotalFluidMatch =
            product.key == ALL_PRODUCT_TYPES.TotalFluid.key &&
            product.key.indexOf(series.product) >= 0;
          const isOnePlusWORMatch =
            series.product === WASM_ONE_PLUS_WOR_PRODUCT &&
            product.key === ALL_PRODUCT_TYPES.OnePlusWOR.key;

          if (isMismatchedProduct && !isTotalFluidMatch && !isOnePlusWORMatch) {
            continue;
          }
          const yAxisTitle = response ? response?.layout?.yAxis?.title : "";
          titles[tw.title] = tw.title;
          temporaryTypeWellSeries.push({
            name: tw.title,
            id: typeWellId + tw.id,
            originalId: tw.id, // used to identify the type well id in the legend
            type: "line",
            kind: "typewell",
            itemStyle: {
              opacity: 1,
              color: tw.color ?? BLACK_HEX
            },
            showSymbol: false,
            lineStyle: { opacity: 1, type: "dashed", width: tw.thickness },
            line: {
              originalColor: tw.color ?? BLACK_HEX,
              originalWidth: tw.thickness
            },
            data: getTypeWellSeries(
              chartSettings.chartType,
              series.rate_cum,
              yAxisTitle,
              chartSettings.timeStep
            )
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        return;
      }
    }

    setTypeWellSeries([...temporaryTypeWellSeries]);
  }

  return { typeWellSeries };
}
